import React from 'react';
import { GetServerSideProps, NextPageWithLayout, Redirect } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { ContentStackPageNames } from '@enums/contentStack';
import CMSPage from '@components/organisms/content-stack/cms-page';
import { IContentTypeEntries } from '@constants/metas/default';
import ContentStackService from '@services/content-stack-service';
import extractFromContext from '@helpers/utils/extract-from-context';
import DefaultLayout from '@layouts/default-layout';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import logger from '@helpers/utils/logger/client';
import { checkShouldShowBanner } from '@helpers/utils/general';
import { getAppInitialProps } from '@helpers/initial-props';

export const pagePathToPageNameMap = new Map([
  ['app-ds-ed', ContentStackPageNames.DS_EXPRESS_DELIVERY_APP_PAGE],
  ['daily-deals', ContentStackPageNames.DAILY_DEALS_PAGE],
  ['my-community', ContentStackPageNames.DAILY_DEALS_PAGE],
  ['top-seller', ContentStackPageNames.TOP_SELLERS_PAGE],
  ['channels', ContentStackPageNames.CHANNEL_PAGE],
  ['description', ContentStackPageNames.LANDING_WITH_DESCRIPTION_AND_PRODUCT_PAGE],
  ['raas', ContentStackPageNames.RAAS_PAGE],
  ['web-ds-ed', ContentStackPageNames.DS_EXPRESS_DELIVERY_WEB_PAGE],
  ['custom', ContentStackPageNames.CUSTOM_PAGE],
  ['homemade-by-vc', ContentStackPageNames.HOME_MADE_BY_VC_PAGE],
  ['l', ContentStackPageNames.CUSTOM_PAGE],
  ['talking-fashion', ContentStackPageNames.TALKING_FASHION_PAGE_WEB_PAGE],
]);

type CMSRootPageProps = {
  contentTypeEntries: IContentTypeEntries;
};

const CMSRootPage: NextPageWithLayout<CMSRootPageProps> = (props) => {
  const { contentTypeEntries } = props;
  const shouldShowBanner = checkShouldShowBanner(contentTypeEntries);

  return (
    <DefaultLayout shouldShowBanner={shouldShowBanner}>
      <CMSPage {...props} />;
    </DefaultLayout>
  );
};

CMSRootPage.Layout = [];
CMSRootPage.PageType = 'CMSPage';

export const getServerSideProps: GetServerSideProps<CMSRootPageProps | Redirect> = async (context) => {
  const { preferences, locale, userId } = extractFromContext(context);
  const [pagePath, ...rest] = context?.params?.cmsSlug as string[];
  const pathName = pagePathToPageNameMap.get(pagePath);

  // If requested path isn't registered as a CMS path, redirect to 404
  if (!pathName) {
    return {
      notFound: true,
    };
  }

  try {
    const initialProps = await getAppInitialProps(context);
    const [translations, contentTypeEntries] = await Promise.all([
      serverSideTranslations(locale, ['default-layout', 'common']),
      ContentStackService.getDataForCMSPage(pathName, { ...preferences, userId }, { pageSlug: rest.at(-1) }),
    ]);

    if (!contentTypeEntries) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        ...initialProps,
        ...translations,
        contentTypeEntries,
      },
    };
  } catch (err) {
    if (isRequestError(err)) {
      if (!isRequest404Error(err)) {
        logger.error(
          err,
          'There was an api request error on the CMS Page - getServerSideProps - [[...cmsSlug]].page.tsx',
        );
      }
      return {
        notFound: true,
      };
    }
    logger.error(err, 'The server failed to render the CMS Page - getServerSideProps - [[...cmsSlug]].page.tsx');
    throw err;
  }
};

export default CMSRootPage;
